import { useEffect, useState } from 'react';
import {
  APPROVAL_VIA,
  APPROVAL_VIA_LABEL,
  type ApprovalVia,
  BENCH_PARAMS,
  INTAKE_CHECKLIST,
  type OrderDetail,
  RACK_TYPE_SHORT,
  RACK_TYPES,
  type RackType,
  type FileRef,
} from '@reikraft/shared';
import { Field, Modal, PhotoGrid, Segmented, useToast } from '@reikraft/ui';
import { api, errorText, uploadFiles } from '../api';
import { Hint } from '../components/common';
import { formatMoney, rubToKop } from '../format';
import { useOrderAction } from './useOrder';

type P = { order: OrderDetail; open: boolean; onClose: () => void };

export function IntakeModal({ order, open, onClose }: P) {
  const [checklist, setChecklist] = useState<string[]>([]);
  const [completeness, setCompleteness] = useState('');
  const [rackType, setRackType] = useState<RackType | null>(order.rackType);
  const [oem, setOem] = useState(order.oem ?? '');
  const [place, setPlace] = useState('');
  const [photos, setPhotos] = useState<FileRef[]>([]);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();
  useEffect(() => {
    if (open) {
      setChecklist(order.checklist);
      setCompleteness(order.completeness ?? '');
      setRackType(order.rackType);
      setOem(order.oem ?? '');
      setPhotos([]);
    }
  }, [open, order]);
  const action = useOrderAction(
    order.id,
    async () => {
      const r = await api.post<OrderDetail>(`/admin/orders/${order.id}/intake`, {
        checklist,
        completeness: completeness || null,
        rackType,
        oem: oem || null,
        place: place || null,
      });
      if (photos.length)
        return api.post<OrderDetail>(`/admin/orders/${order.id}/photos`, { fileIds: photos.map((p) => p.id), role: 'intake' });
      return r;
    },
    (o) => `Рейка принята, бирка ${o.unit?.code ?? ''}`,
  );
  const add = async (files: FileList) => {
    setUploading(true);
    try {
      setPhotos([...photos, ...(await uploadFiles(files))]);
    } catch (e) {
      toast(errorText(e), { error: true });
    } finally {
      setUploading(false);
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Приёмка · ${order.number}`}
      wide
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="rk-btn rk-btn--primary"
            disabled={action.isPending || uploading}
            onClick={() => action.mutate(undefined, { onSuccess: onClose })}
          >
            Принять рейку
          </button>
        </>
      }
    >
      <Hint>Рейка получит бирку с коротким кодом — распечатайте её из меню «Документы» и наклейте на корпус.</Hint>
      <Field label="Тип рейки">
        <Segmented
          label="Тип рейки"
          value={rackType}
          onChange={setRackType}
          options={RACK_TYPES.map((t) => ({ value: t, label: RACK_TYPE_SHORT[t] }))}
        />
      </Field>
      <div className="rk-grid-form">
        <Field label="Номер на корпусе (OEM)">
          <input className="rk-input rk-input--mono" value={oem} onChange={(e) => setOem(e.target.value)} />
        </Field>
        <Field label="Место на складе" hint="Например, A-03">
          <input className="rk-input rk-input--mono" value={place} onChange={(e) => setPlace(e.target.value)} />
        </Field>
      </div>
      <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
        <legend className="rk-label" style={{ marginBottom: 6 }}>
          Комплектность
        </legend>
        <div className="adm-checks">
          {INTAKE_CHECKLIST.map((c) => (
            <label key={c} className="rk-check">
              <input
                type="checkbox"
                checked={checklist.includes(c)}
                onChange={(e) => setChecklist(e.target.checked ? [...checklist, c] : checklist.filter((x) => x !== c))}
              />
              {c}
            </label>
          ))}
        </div>
      </fieldset>
      <Field label="Состояние и замечания">
        <input
          className="rk-input"
          value={completeness}
          onChange={(e) => setCompleteness(e.target.value)}
          placeholder="Сколы на корпусе, порван пыльник…"
        />
      </Field>
      <Field label="Фото при приёмке" hint="Со всех сторон — защита от споров о состоянии">
        <PhotoGrid
          photos={photos}
          onAdd={add}
          uploading={uploading}
          capture
          onRemove={(id) => setPhotos(photos.filter((p) => p.id !== id))}
          addLabel="Снять фото"
        />
      </Field>
    </Modal>
  );
}

export function ApproveModal({ order, open, onClose }: P) {
  const [via, setVia] = useState<ApprovalVia>(order.source === 'avito' ? 'avito' : 'phone');
  const [byName, setByName] = useState('');
  const action = useOrderAction(
    order.id,
    () => api.post<OrderDetail>(`/admin/orders/${order.id}/estimate/approve`, { via, byName: byName || null }),
    'Смета согласована, заказ в ремонте',
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Клиент согласовал смету"
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="rk-btn rk-btn--primary"
            disabled={action.isPending}
            onClick={() => action.mutate(undefined, { onSuccess: onClose })}
          >
            Согласовано · {formatMoney(order.estimate.totalKop)}
          </button>
        </>
      }
    >
      <Hint>Зафиксируем, как и с кем согласовали сумму — это защита от споров «мы на такую сумму не договаривались».</Hint>
      <Field label="Как согласовали">
        <select className="rk-select" value={via} onChange={(e) => setVia(e.target.value as ApprovalVia)}>
          {APPROVAL_VIA.filter((v) => v !== 'link' && v !== 'cabinet').map((v) => (
            <option key={v} value={v}>
              {APPROVAL_VIA_LABEL[v]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Кто согласовал" hint="Имя и должность, если это организация">
        <input className="rk-input" value={byName} onChange={(e) => setByName(e.target.value)} />
      </Field>
    </Modal>
  );
}

export function DeclineModal({ order, open, onClose }: P) {
  const [reason, setReason] = useState('');
  const [buyout, setBuyout] = useState(false);
  const [price, setPrice] = useState('');
  const action = useOrderAction(
    order.id,
    () =>
      api.post<OrderDetail>(`/admin/orders/${order.id}/estimate/decline`, {
        reason: reason || null,
        buyout,
        buyoutKop: buyout ? rubToKop(price) : null,
      }),
    'Отказ оформлен',
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Отказ от ремонта"
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="rk-btn rk-btn--danger"
            disabled={action.isPending}
            onClick={() => action.mutate(undefined, { onSuccess: onClose })}
          >
            Оформить отказ
          </button>
        </>
      }
    >
      <Field label="Причина">
        <input
          className="rk-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Дорого, купит новую, продал машину…"
        />
      </Field>
      <label className="rk-check">
        <input type="checkbox" checked={buyout} onChange={(e) => setBuyout(e.target.checked)} />
        Выкупаем рейку у клиента — она уйдёт в обменный фонд
      </label>
      {buyout && (
        <Field label="Цена выкупа, ₽">
          <input className="rk-input rk-input--mono" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
      )}
      <Hint>Сумма заказа станет равна цене диагностики из настроек — её можно поправить в карточке.</Hint>
    </Modal>
  );
}

export function CancelModal({ order, open, onClose }: P) {
  const [reason, setReason] = useState('');
  const action = useOrderAction(
    order.id,
    () => api.post<OrderDetail>(`/admin/orders/${order.id}/status`, { to: 'cancelled', reason: reason || null }),
    'Заказ отменён',
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Отменить заказ"
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Не отменять
          </button>
          <button
            type="button"
            className="rk-btn rk-btn--danger"
            disabled={action.isPending}
            onClick={() => action.mutate(undefined, { onSuccess: onClose })}
          >
            Отменить заказ
          </button>
        </>
      }
    >
      <Field label="Причина" hint="Видно в статистике: почему срываются обращения">
        <input
          className="rk-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Не дозвонились, передумал, дорого…"
        />
      </Field>
      {order.issuedUnit && <Hint>Резерв рейки из фонда будет снят.</Hint>}
    </Modal>
  );
}

type BenchRow = { name: string; tolerance: string; value: string; result: 'ok' | 'fail' };
const benchDefaults = (): BenchRow[] => BENCH_PARAMS.map((b) => ({ name: b.name, tolerance: b.tolerance, value: '', result: 'ok' }));

export function BenchModal({ order, open, onClose }: P) {
  const [params, setParams] = useState<BenchRow[]>(benchDefaults);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setParams(benchDefaults());
      setNote('');
    }
  }, [open]);
  const passed = params.every((p) => p.result === 'ok');
  const action = useOrderAction(
    order.id,
    () => api.post<OrderDetail>(`/admin/orders/${order.id}/bench`, { params, note: note || null }),
    (o) => (o.bench[0]?.passed ? `Стенд пройден · ${o.bench[0].number}` : 'Стенд не пройден — заказ вернулся в ремонт'),
  );
  const setP = (i: number, patch: Partial<BenchRow>) => setParams(params.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Протокол стендовых испытаний"
      wide
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className={passed ? 'rk-btn rk-btn--primary' : 'rk-btn rk-btn--danger'}
            disabled={action.isPending}
            onClick={() => action.mutate(undefined, { onSuccess: onClose })}
          >
            {passed ? 'Стенд пройден' : 'Не прошла стенд'}
          </button>
        </>
      }
    >
      <div className="rk-table-wrap">
        <div
          className="rk-table"
          style={{ ['--rk-table-min' as string]: '620px', ['--rk-cols' as string]: 'minmax(180px,1.4fr) 110px 110px 150px' }}
        >
          <div className="rk-tr rk-tr--head">
            <span>Параметр</span>
            <span>Допуск</span>
            <span>Значение</span>
            <span>Итог</span>
          </div>
          {params.map((p, i) => (
            <div key={i} className="rk-tr" style={{ padding: '8px 16px' }}>
              <input className="rk-input" value={p.name} onChange={(e) => setP(i, { name: e.target.value })} aria-label="Параметр" />
              <input
                className="rk-input rk-input--mono"
                value={p.tolerance}
                onChange={(e) => setP(i, { tolerance: e.target.value })}
                aria-label="Допуск"
                placeholder="бар, Н·м, мм"
              />
              <input
                className="rk-input rk-input--mono"
                value={p.value}
                onChange={(e) => setP(i, { value: e.target.value })}
                aria-label="Значение"
              />
              <Segmented
                label="Итог"
                value={p.result}
                onChange={(v) => setP(i, { result: v })}
                options={[
                  { value: 'ok', label: 'ОК' },
                  { value: 'fail', label: 'НЕТ' },
                ]}
              />
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="rk-btn rk-btn--sm"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => setParams([...params, { name: '', tolerance: '', value: '', result: 'ok' }])}
      >
        + Параметр
      </button>
      <Field label="Примечание">
        <input className="rk-input" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

export function EstimateLinkModal({ link, onClose }: { link: string | null; onClose: () => void }) {
  const toast = useToast();
  return (
    <Modal
      open={!!link}
      onClose={onClose}
      title="Ссылка на смету для клиента"
      footer={
        <button type="button" className="rk-btn rk-btn--primary" onClick={onClose}>
          Готово
        </button>
      }
    >
      <Hint>Отправьте ссылку клиенту в мессенджер или чат Авито. Ссылка действует 14 дней; повторная отправка сметы создаёт новую.</Hint>
      <input className="rk-input rk-input--mono" readOnly value={link ?? ''} onFocus={(e) => e.target.select()} />
      <button
        type="button"
        className="rk-btn"
        onClick={async () => {
          await navigator.clipboard.writeText(link ?? '');
          toast('Ссылка скопирована');
        }}
      >
        Скопировать ссылку
      </button>
    </Modal>
  );
}
